import React, { useEffect } from "react";
import { connect } from "react-redux";
import { Redirect } from "react-router-dom";
import { parse } from "query-string";
import { UserRoles } from "../../enums/userRoles";

import LoadingPage from "./LoadingPage";

import I18n from "../../I18n";

import { GlobalProvider as ColbiUIProvider } from "colbi_web_ui/lib/state/GlobalProvider";

import {
    login,
    forgetPassword,
    setLocale,
    logout,
    checkValidSovosIdentity,
} from "../../store/actions";

import { GlobalProvider } from "../../state/globalProvider";

import { getLocale } from "../../utils/locale";

import { REACT_APP_COLBI_THEME, REACT_APP_AUTH_MODE } from "../../environmentVariables";
import { useAuth, hasAuthParams } from "react-oidc-context";
import { decodeUserSession, logoutFromSSO } from "../../utils/utils";

import LoginLegacy from "./LoginLegacy";

const Provider = ({ children, ...props }) => {
    return (
        <GlobalProvider {...props}>
            <ColbiUIProvider {...props}>{children}</ColbiUIProvider>
        </GlobalProvider>
    );
};

const Login = (props) => {
    const appTheme = REACT_APP_COLBI_THEME
        ? REACT_APP_COLBI_THEME.toLocaleLowerCase()
        : document.localStorage.getItem("theme")
          ? document.localStorage.getItem("theme")
          : "sovos";

    const root = document.getElementsByTagName("html")[0];
    root.setAttribute("class", appTheme);

    const { location, match, user } = props;

    const userInformation = decodeUserSession(user);

    const auth = useAuth();
    const locale = getLocale(auth, match);

    const i18n = I18n.use(locale);
    setLocale(locale);

    useEffect(() => {
        if (REACT_APP_AUTH_MODE === "SSO") {
            const queryParams = {};
            if (userInformation) {
                queryParams.login_hint = userInformation?.loginHint;
            }
            if (appTheme) {
                queryParams.extraQueryParams = {
                    theme_key: appTheme,
                };
            }
            if (
                !hasAuthParams(location) &&
                !auth?.isAuthenticated &&
                !auth?.isLoading &&
                !auth?.error
            ) {
                auth.clearStaleState().then(async () => {
                    window.localStorage.clear();
                    await auth?.signinRedirect(queryParams);
                });
            }

            if (!auth?.isLoading && auth?.isAuthenticated && !userInformation) {
                logoutFromSSO(auth);
            }
        }
    }, [auth, userInformation, appTheme, location]);

    if (REACT_APP_AUTH_MODE === "LEGACY") {
        return <LoginLegacy {...props} />;
    }

    if (!auth?.isLoading && auth?.error) {
        console.error(auth.error);
        logout(auth);
        return <Redirect to={`/${locale}/unauthorized`} />;
    }

    if (userInformation && !auth?.isLoading && auth?.isAuthenticated) {
        const organizationsUser = Object.keys(userInformation.organizationsRoles || {});
        const { redirect } = parse(location.search);
        const defaultOrgToNavigate = organizationsUser?.length ? organizationsUser[0] : null;
        return (
            <Redirect
                to={
                    redirect || parseInt(userInformation.expired)
                        ? `/${locale}/reset_password`
                        : userInformation.role === UserRoles.BACKOFFICE
                          ? `/${locale}/settings/`
                          : defaultOrgToNavigate
                            ? `/${locale}/organizations/${defaultOrgToNavigate}`
                            : `/${locale}/`
                }
            />
        );
    }

    return (
        <Provider
            {...{
                i18n,
                location,
            }}
        >
            <LoadingPage {...props} />
        </Provider>
    );
};

Login.queries = () => [
    {
        resource: "application_version",
        body: "version",
    },
    {
        resource: "email_service_active",
        body: "active",
        forceRefresh: true,
    },
];

export default connect(
    ({ user, fetch, loginError, forgetPasswordMessage }) => ({
        user,
        fetch,
        loginError,
        forgetPasswordMessage,
    }),
    {
        login,
        forgetPassword,
        setLocale,
        logout,
        checkValidSovosIdentity,
    },
)(Login);
